'use client'

import React from 'react'
import Image from 'next/image'
import { Box, Flex, SimpleGrid, Text } from '@mantine/core'
import { motion } from 'framer-motion'
import { refreshTextVariantProps } from '@/components/Refresh/RefreshTypography'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { staggerContainer, staggerItem, textFadeInVariantsNoTransition } from '@/lib/animations'
import { MotionBox } from '@/components/shared/AnimatedComponents'
import { parseHtml } from '@/lib/parseHtml'
import type { SkuComponentConfigMap } from '@/types/enums/sku-component-types'
import CategoryStatsImg from '@/assets/images/refresh/categories/weight-loss/statistic.webp'

const MIN_HEIGHT_DESKTOP = 500
const MIN_HEIGHT_MOBILE = 300
const DIVIDER_COLOR = 'rgba(43, 42, 42, 0.22)'

const DEFAULT_IMAGE_ALT = 'Clinical outcomes and care'

export function SkuStats({ config }: { config: SkuComponentConfigMap['skuStats'] }) {
    const { isSm, isMd } = useBreakpoint()
    const { description, stats, imageAlt = DEFAULT_IMAGE_ALT, megaStat } = config
    const isTwoColumn = stats.length === 2 && !isSm && !isMd

    return (
        <Box
            id="skuStats"
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px 0px' }}
            variants={staggerContainer}
            p={{ base: 24, sm: 48 }}
            maw={1440}
            mx="auto"
            w="100%"
            style={{
                background: 'rgba(43, 42, 42, 0.08)',
                borderRadius: 24,
                minWidth: 0,
            }}
        >
            <Flex direction={isSm ? 'column' : 'row'} gap={60} align={isSm ? 'stretch' : 'flex-start'} style={{ minWidth: 0 }}>
                <Box
                    style={{
                        flex: isSm ? 'none' : 1,
                        width: isSm ? '100%' : undefined,
                        minWidth: 0,
                        minHeight: isSm ? MIN_HEIGHT_MOBILE : MIN_HEIGHT_DESKTOP,
                        maxHeight: isSm ? undefined : 500,
                        position: 'relative',
                        borderRadius: 16,
                        overflow: 'hidden',
                    }}
                >
                    <Image
                        src={CategoryStatsImg}
                        alt={imageAlt}
                        fill
                        style={{ objectFit: 'cover' }}
                        sizes={isSm ? '100vw' : '(max-width: 1440px) 50vw, 720px'}
                    />
                    {megaStat && (
                        <motion.span
                            variants={staggerItem}
                            style={{
                                display: 'block',
                                position: 'absolute',
                                bottom: 24,
                                left: 24,
                                fontSize: 130,
                                fontWeight: 450,
                                lineHeight: '80%',
                                letterSpacing: -6.5,
                                color: 'white',
                            }}
                        >
                            {megaStat}
                        </motion.span>
                    )}
                </Box>

                <Flex
                    flex={1}
                    direction="column"
                    justify={isSm ? undefined : 'center'}
                    gap={32}
                    style={{ minWidth: 0 }}
                >
                    <MotionBox
                        variants={textFadeInVariantsNoTransition}
                        style={{
                            marginTop: isSm ? -32 : 0,
                            zIndex: 1,
                        }}
                    >
                        <Text
                            {...refreshTextVariantProps.body}
                            component="div"
                            fz="var(--gm-refresh-title-md)"
                            fw={400}
                            py={0}
                            lh="100%"
                            c="gray.9"
                            lts={-0.8}
                            m={0}
                        >
                            {description}
                        </Text>
                    </MotionBox>

                    <MotionBox
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: '-50px 0px' }}
                    >
                        <SimpleGrid cols={isTwoColumn ? 2 : 1} spacing={24} verticalSpacing={24}>
                            {stats.map((statHtml, index) => (
                                <MotionBox key={`stat-${index}`} variants={staggerItem}>
                                    <Text
                                        {...refreshTextVariantProps.body}
                                        component="div"
                                        pt={8}
                                        c="gray.6"
                                        size="md"
                                        fw={450}
                                        lh="130%"
                                        style={{ borderTop: `1px solid ${DIVIDER_COLOR}` }}
                                    >
                                        {parseHtml(statHtml)}
                                    </Text>
                                </MotionBox>
                            ))}
                        </SimpleGrid>
                    </MotionBox>
                </Flex>
            </Flex>
        </Box>
    )
}
